import {
  LegacyGUID,
  LegacyInstanceDetails,
  LegacyInstanceResponse,
  LegacyListResponse,
} from '@verantos-dev/types-common';

import { SubjectVariableCriteriaSelectionTypeLegacy } from '..';

export type StudyArmsListResponsePayloadLegacy = LegacyListResponse<StudyArmsResponsePayloadLegacy>;
export type StudyArmResponseLegacy = LegacyInstanceResponse<StudyArmResponseDataLegacy>;

export type StudyArmsResponsePayloadLegacy = {
  /**
   * The number of subjects in the cohort.
   */
  subjectCount: number;

  /**
   * The number of child cohorts.
   */
  childCohortCount: number;

  /**
   * can the cohort be downloaded
   */
  isDownloadable: boolean;
} & BaseCohortResponsePayloadLegacy;

/**
 * The base response payload for a cohort.
 */
export interface BaseCohortResponsePayloadLegacy extends LegacyInstanceDetails {
  /**
   * The ID of the study to which the cohort belongs.
   */
  studyId: LegacyGUID;

  /**
   * The ID of the cohort.
   */
  cohortId: LegacyGUID;

  /**
   * The name of the cohort.
   */
  cohortName: string;

  /**
   * The runtime version of the cohort.
   */
  runtimeVersion: string;

  /**
   * The type of the cohort.
   */
  cohortType: CohortTypeLegacy;

  /**
   * The status of the cohort.
   */
  cohortStatus: CohortStatusLegacy;

  /**
   * The number of subjects in the cohort.
   */
  subjectCount?: number;
}

export type StudyArmResponseDataLegacy = {
  cohortId: string;
  studyId: string;
  cohortName: string;
  cohortType: CohortTypeLegacy.STUDY_ARM;
  cohortStatus: CohortStatusLegacy;
  subjectCount: number;
  childCohortCount: number;
  isDownloadable: boolean;
  isHTMLDownloadable: boolean;
  runtimeVersion: string;
  cohortDefinition: StudyArmDefinitionPayloadLegacy;
} & LegacyInstanceDetails;

export type StudyArmDefinitionPayloadLegacy = {
  cohortDSL: string;
  indexDate: IndexDatePayloadLegacy | undefined;
  subjectVariables: SubjectVariablePayloadLegacy[];
  simpleEvents: SimpleEventPayloadLegacy[];
  derivedEvents: DerivedEventPayloadLegacy[];
  studyPeriodRestrictions: StudyPeriodRestrictionsPayloadLegacy | undefined;
};

export type IndexDatePayloadLegacy = {
  indexDateId: string;
  indexDateType: IndexDateTypePayloadLegacy;
  indexDateValue:
    | IndexDateCalendarDatePayloadLegacy
    | IndexDateEventInstancePayloadLegacy
    | IndexDateAttributePayloadLegacy;
  indexDateRangeValue?: IndexDateRangePayloadLegacy;
};

export type IndexDateRangePayloadLegacy = {
  startDate?: string;
  endDate?: string;
};

export type IndexDateCalendarDatePayloadLegacy = {
  dateValue: string;
};

export type IndexDateEventInstancePayloadLegacy = {
  eventId: string;
  eventName: string;
};

export type IndexDateAttributePayloadLegacy = {
  attribute: string;
};

export type SubjectVariablePayloadLegacy = {
  subjectVariableId: string;
  subjectVariableName: string;
  subjectVariableType: SubjectVariableTypePayloadLegacy;
  conceptFilters: FilterPayloadLegacy[];
  numericFilters: FilterPayloadLegacy[];
  countFilter: FilterPayloadLegacy | undefined;
  criteriaSelection: SubjectVariableCriteriaSelectionTypeLegacy;
};

export type SimpleEventPayloadLegacy = {
  eventId: string;
  eventName: string;
  eventEntityType: EventEntityTypeLegacy;
  conceptFilters: FilterPayloadLegacy[]; // only used in simple events
  numericFilters: FilterPayloadLegacy[]; // only used in simple events
  relatedEntityFilters: FilterPayloadLegacy[]; // only used in simple events
  uniquenessFilters: FilterPayloadLegacy[]; // only used in simple events
};

export type DerivedEventPayloadLegacy = {
  eventId: string;
  eventName: string;
  eventModelType: EventModelTypeLegacy;
  eventFilters: EventFilterPayloadLegacy[];
  anyOrder?: boolean; // only applicable for SEQUENCE events
  temporalConstraint?: FilterPayloadLegacy[]; // only applicable for SEQUENCE events
  uniquenessFilters?: FilterPayloadLegacy[]; // only applicable for MERGED events
};

export type StudyPeriodRestrictionsPayloadLegacy = {
  overallStudyPeriodRestriction: OverallStudyPeriodRestrictionPayloadLegacy | undefined;
  baselinePeriodRestriction: BaselinePeriodRestrictionPayloadLegacy | undefined;
  followUpPeriodRestriction: FollowUpPeriodRestrictionPayloadLegacy | undefined;
};

export type OverallStudyPeriodRestrictionPayloadLegacy = {
  startDate: string | undefined;
  endDate: string | undefined;
};

export type BaselinePeriodRestrictionPayloadLegacy = {
  daysBeforeIndexDate: number | undefined;
};

export type FollowUpPeriodRestrictionPayloadLegacy = {
  daysAfterIndexDate: number | undefined;
};

export type EventFilterPayloadLegacy = {
  eventFilterId: string;
  referencedEventId: string;
  referencedEventName: string;
  dateAttribute: string;
  selected?: boolean;
  position?: EventPositionTypePayloadLegacy;
  index?: number;
};

export type FilterPayloadLegacy = {
  filterId: string;
  inputFields: InputFieldPayloadLegacy[];
};

export type InputFieldPayloadLegacy = {
  inputFieldId: string;
  inputFieldValue: string;
};

export enum CohortStatusLegacy {
  DRAFT = 'DRAFT',
  FINAL = 'FINAL',
  PROCESSED = 'PROCESSED',
  PROCESSING = 'PROCESSING',
  ERROR = 'ERROR',
}

export enum CohortTypeLegacy {
  STUDY_ARM = 'STUDY_ARM',
  SUBGROUP = 'SUBGROUP',
}

export enum SubjectVariableTypePayloadLegacy {
  DEMOGRAPHIC = 'DEMOGRAPHIC',
  COUNT = 'COUNT',
}

export enum IndexDateTypePayloadLegacy {
  CALENDAR_DATE = 'CALENDAR_DATE',
  EVENT_INSTANCE = 'EVENT_INSTANCE',
}

export enum EventModelTypeLegacy {
  MERGED = 'MERGED',
  SEQUENCE = 'SEQUENCE',
  INSTANCE = 'INSTANCE',
}

export enum EventEntityTypeLegacy {
  CONDITION_OCCURRENCE = 'CONDITION_OCCURRENCE',
  DRUG_EXPOSURE = 'DRUG_EXPOSURE',
  MEASUREMENT = 'MEASUREMENT',
  OBSERVATION = 'OBSERVATION',
  PROCEDURE_OCCURRENCE = 'PROCEDURE_OCCURRENCE',
  VISIT_OCCURRENCE = 'VISIT_OCCURRENCE',
}

export enum EventPositionTypePayloadLegacy {
  MAX = 'MAX',
  MIN = 'MIN',
  INDEX = 'INDEX',
}
